import React, { useState } from "react";
import {
  Row,
  Col,
  Select,
  Checkbox,
  Radio,
  Drawer,
  Tag,
  Typography,
} from "antd";
import styles from "./filterSort.module.scss";
import {
  FILTERS_KEYS,
  SORT_KEYS,
  sortOptions,
  // SIZE_RANGE,
  DISTANCE_RANGE_MAX,
  FILTERS_ICONS,
  FILTERS_LABELS,
  SORT_ICONS,
} from "./constants/constants";
// OPTIMIZED: Replaced @ant-design/icons and @fortawesome with lightweight SVGs
import { SwapIcon, CloseCircleIcon, FilterIcon } from "../Icons";
import getUserLocation from "../../utils/location";
import { useUserLocation } from "../Location/LocationContext";
import { showAlertModal } from "../alertModal";
import DistanceSliderRange from "./distanceRange";
const { Option } = Select;

interface FilterSortProps {
  filters: {
    filters?: FILTERS_KEYS[];
    sortBy: SORT_KEYS | undefined;
    // sizeRange: number[];
    distanceRange: number;
    selectedArea: string | null;
  };
  setFilters: React.Dispatch<
    React.SetStateAction<{
      filters?: FILTERS_KEYS[];
      sortBy: SORT_KEYS | undefined;
      // sizeRange: number[];
      distanceRange: number;
      selectedArea: string | null;
    }>
  >;
  activeTab?: string;
  handleTabChange?: (key: string) => void;
  areas?: { id: string; name: string }[];
}

const FilterSort = ({ filters, setFilters, activeTab, handleTabChange, areas }: FilterSortProps) => {
  const [sortDrawerVisible, setSortDrawerVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

  const [selectedSort, setSelectedSort] = useState<SORT_KEYS | null>(null);
  const { userLocation, setUserLocation } = useUserLocation();

  const handleFilterChange = (checkedValues: any) => {
    setFilters((prev) => ({
      ...prev,
      filters: checkedValues,
    }));
  };

  const handleAreaChange = (value: string | null | undefined) => {
    setFilters((prev) => ({
      ...prev,
      selectedArea: value ?? null,
    }));
  };

  const clearArea = () => {
    setFilters((prev) => ({
      ...prev,
      selectedArea: null,
    }));
  };

  const handleSortChange = (value: SORT_KEYS) => {
    if (
      value === SORT_KEYS.DISTANCE_NEAREST &&
      (!userLocation?.latitude || !userLocation?.longitude)
    ) {
      // Check mobile on-demand to avoid hydration flash
      const isMobileCheck = typeof window !== 'undefined' && window.innerWidth <= 767;
      getUserLocation(setUserLocation, showAlertModal({ isMobile: isMobileCheck }));
      return;
    }

    setSelectedSort(value);
    if (value) {
      setFilters((prev) => ({
        ...prev,
        sortBy: value,
      }));
    }
    onCloseDrawer();
  };

  const showFilterDrawer = () => {
    setFilterDrawerVisible(true);
  };

  const onCloseFilterDrawer = () => {
    setFilterDrawerVisible(false);
  };

  const showSortDrawer = () => {
    setSortDrawerVisible(true);
  };

  const onCloseDrawer = () => {
    setSortDrawerVisible(false);
  };

  const clearSort = () => {
    setSelectedSort(null);
    setFilters((prev) => ({
      ...prev,
      sortBy: SORT_KEYS.DISTANCE_NEAREST, // Resetting sort by to default
    }));
  };

  // const clearSizeRange = () => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     sizeRange: [SIZE_RANGE.MIN, SIZE_RANGE.MAX],
  //   }));
  // };

  const clearDistanceRange = () => {
    setFilters((prev) => ({
      ...prev,
      distanceRange: DISTANCE_RANGE_MAX,
    }));
  };

  const handleDistanceChange = (value: number) => {
    setFilters((prev) => ({
      ...prev,
      distanceRange: value,
    }));
  };

  const { Text, Title } = Typography;

  return (
    <>
      {/* Mobile Layout - Hidden on desktop via CSS */}
      <div className={styles.mobileOnly}>
        <div className={styles.filterSortContainer}>
          <button
            type="button"
            onClick={showSortDrawer}
            className={styles.sortFilterText}
            aria-label="Open sort options"
          >
            Sort{" "}
            <SwapIcon
              size={14}
              style={{
                transform: "rotate(90deg)",
              }}
            />
          </button>
          <button
            type="button"
            onClick={showFilterDrawer}
            className={styles.sortFilterText}
            aria-label="Open filter options"
          >
            Filter <FilterIcon size={14} />
          </button>
        </div>
        {/* filter.sizeRange commented out */}
        {(selectedSort || filters.filters || filters.selectedArea) && (
          <div className={styles.tag}>
            {selectedSort && (
              <Tag
                closable
                onClose={clearSort}
                className={styles.selectedSortTag}
              >
                {
                  sortOptions.find((option) => option.key === selectedSort)
                    ?.label
                }
              </Tag>
            )}
            {filters.filters?.map((filter: FILTERS_KEYS) => (
              <Tag
                key={filter}
                closable
                className={styles.selectedSortTag}
                onClose={() =>
                  handleFilterChange(
                    filters.filters?.filter((f) => f !== filter)
                  )
                }
              >
                {React.createElement(FILTERS_ICONS[filter], { size: 14, style: { marginRight: '4px' } })} {FILTERS_LABELS[filter]}
              </Tag>
            ))}
            {filters.selectedArea && (
              <Tag
                closable
                onClose={clearArea}
                className={styles.selectedSortTag}
              >
                {filters.selectedArea}
              </Tag>
            )}
            {/* Size range filter tag disabled
            {!!filters.sizeRange?.length &&
              !(
                filters.sizeRange[0] === SIZE_RANGE.MIN &&
                filters.sizeRange[1] === SIZE_RANGE.MAX
              ) && (
                <Tag
                  closable
                  onClose={clearSizeRange}
                  className={styles.selectedSortTag}
                >
                  <span>
                    {filters.sizeRange[0]} - {filters.sizeRange[1]}
                  </span>
                </Tag>
              )} */}
            {!!filters.distanceRange &&
              !(filters.distanceRange === DISTANCE_RANGE_MAX) && (
                <Tag
                  closable
                  onClose={clearDistanceRange}
                  className={styles.selectedSortTag}
                >
                  <span>within {filters.distanceRange} km</span>
                </Tag>
              )}
          </div>
        )}
      </div>

      {/* Desktop Layout - Hidden on mobile via CSS */}
      <div className={styles.desktopOnly}>
        <div className={styles.filterSortContainer}>
          <Row gutter={[16, 16]} className={styles.sortRow}>
            <Col span={24}>
              <Title level={3}>Sort By</Title>
              <Select
                value={filters.sortBy}
                onChange={handleSortChange}
                className={styles.sortSelect}
              >
                {sortOptions.map(({ key, label }) => (
                  <Option value={key} key={key}>
                    {React.createElement(SORT_ICONS[key], { size: 14, style: { marginRight: '8px' } })}
                    {label}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className={styles.filterOptions}>
            <Col span={24}>
              <Title level={3}>Filters</Title>
              <Checkbox.Group
                onChange={handleFilterChange}
                value={filters.filters}
                className={styles.filterCheckboxes}
              >
                {Object.entries(FILTERS_KEYS).map(([key, value]) => (
                  <Checkbox key={key} value={key}>
                    {React.createElement(FILTERS_ICONS[key as FILTERS_KEYS], { size: 14, style: { marginRight: '4px' } })} {FILTERS_LABELS[key as FILTERS_KEYS]}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Col>
          </Row>

          {/* <div style={{ marginTop: "10px" }}>
            <SizeRangeSlider
              sizeRange={filters.sizeRange}
              handleSizeChange={handleSizeChange}
            />
          </div> */}

          <div style={{ marginTop: "10px" }}>
            <DistanceSliderRange
              distanceRange={filters.distanceRange}
              handleDistanceChange={handleDistanceChange}
            />
          </div>

          {areas && areas.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <Title level={3}>Area</Title>
              <Select
                allowClear
                showSearch
                placeholder="Select Area"
                value={filters.selectedArea}
                onChange={handleAreaChange}
                className={styles.sortSelect}
                style={{ width: '100%' }}
                filterOption={(input, option: any) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {areas.map((area) => (
                  <Option value={area.name} key={area.id} label={area.name}>
                    {area.name}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Title level={3}>Sort by</Title>
            <span onClick={() => onCloseDrawer()}>
              <CloseCircleIcon size={16} />
            </span>
          </div>
        }
        placement="bottom"
        closable={false}
        onClose={onCloseDrawer}
        open={sortDrawerVisible}
        height="60%"
      >
        <Radio.Group
          onChange={(e) => handleSortChange(e.target.value)}
          value={filters.sortBy}
          className={styles.sortOptions}
        >
          {sortOptions.map(({ key, label }) => (
            <Radio key={key} value={key}>
              {React.createElement(SORT_ICONS[key], { size: 14, style: { marginRight: '8px' } })}
              <Text strong>{label}</Text>
            </Radio>
          ))}
        </Radio.Group>
      </Drawer>

      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Title level={3}>Filter</Title>
            <span onClick={() => onCloseFilterDrawer()}>
              <CloseCircleIcon size={16} />
            </span>
          </div>
        }
        placement="bottom"
        closable={false}
        onClose={onCloseFilterDrawer}
        open={filterDrawerVisible}
        height="60%"
        className={styles.filterDrawerContainer}
      >
        <Checkbox.Group
          onChange={handleFilterChange}
          value={filters.filters}
          className={styles.filterCheckboxes}
        >
          {Object.entries(FILTERS_KEYS).map(([key, value]) => (
            <Checkbox key={key} value={key}>
              {React.createElement(FILTERS_ICONS[key as FILTERS_KEYS], { size: 14, style: { marginRight: '4px' } })} {FILTERS_LABELS[key as FILTERS_KEYS]}
            </Checkbox>
          ))}
        </Checkbox.Group>

        {/* Range to select the size */}
        {/* <div style={{ marginTop: "10px" }}>
          <SizeRangeSlider
            sizeRange={filters.sizeRange}
            handleSizeChange={handleSizeChange}
          />
        </div>   */}

        <div style={{ marginTop: "20px" }}>
          <DistanceSliderRange
            distanceRange={filters.distanceRange}
            handleDistanceChange={handleDistanceChange}
          />
        </div>

        {areas && areas.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <Title level={4}>Select Area</Title>
            <Select
              allowClear
              showSearch
              placeholder="Select Area"
              value={filters.selectedArea}
              onChange={handleAreaChange}
              style={{ width: '100%' }}
              filterOption={(input, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {areas.map((area) => (
                <Option value={area.name} key={area.id} label={area.name}>
                  {area.name}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default FilterSort;
